import axios from 'axios';
import moment from 'moment';
import { decodeUpstoxFeed } from './upstox_proto';

// Upstox API v2 base URLs
const UPSTOX_API_URL = 'https://api.upstox.com/v2';

// Server endpoints (credentials stay server-side)
const API_BASE = process.env.VUE_APP_API_BASE || '';

// Timeframe mapping: app timeframes → Upstox interval values
// Upstox supports: 1minute, 30minute, day, week, month
// For intraday: 1minute, 30minute (current day only)
// For historical: 1minute (1 month), 30minute (1 year), day (1 year), week (10 years), month (10 years)
const TIMEFRAME_MAP = {
    '1m': '1minute',
    '3m': '1minute',   // Use 1min and aggregate
    '5m': '1minute',   // Use 1min and aggregate
    '10m': '1minute',  // Use 1min and aggregate
    '15m': '1minute',  // Use 1min and aggregate
    '20m': '30minute', // Use 30min and aggregate
    '30m': '30minute',
    '1H': '30minute',  // Use 30min and aggregate
    '2H': '30minute',  // Use 30min and aggregate
    '4H': '30minute',  // Use 30min and aggregate
    '1D': 'day',
    '1W': 'week',
    '1M': 'month'
};

// Aggregation factors for timeframes that need combining
const AGGREGATION_MAP = {
    '3m': 3,    // 3 x 1min candles
    '5m': 5,    // 5 x 1min candles
    '10m': 10,  // 10 x 1min candles
    '15m': 15,  // 15 x 1min candles
    '20m': 1,   // No aggregation needed (use 30min directly, close enough)
    '1H': 2,    // 2 x 30min candles
    '2H': 4,    // 4 x 30min candles
    '4H': 8     // 8 x 30min candles
};

// Supported timeframes for UI
export const UPSTOX_SUPPORTED_TFS = ['1m', '5m', '15m', '30m', '1H', '2H', '4H', '1D', '1W', '1M'];

export default {
    accessToken: null,
    _pendingState: null,
    wsConnection: null,
    symbols: [],
    reconnectAttempts: 0,
    _wsOnMessage: null,
    _wsOnError: null,
    _wsSymbols: null,

    // ─── Authentication (server-side credentials) ───

    /**
     * Get the Upstox OAuth login URL from the server.
     * The server builds the URL with redirect_uri pointing to /api/upstox/callback.
     */
    async getAuthUrl() {
        const token = await this._getClerkToken();
        const response = await axios.get(API_BASE + '/api/upstox/auth-url', {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.data && response.data.success) {
            this._pendingState = response.data.state;
            return response.data.authUrl;
        }
        throw new Error(response.data?.error || 'Failed to get auth URL');
    },

    /**
     * Complete the OAuth flow after popup closes.
     * Server exchanges auth code for access_token and stores in Clerk metadata.
     */
    async completeLogin(manualState) {
        const token = await this._getClerkToken();
        const state = manualState || this._pendingState;

        if (!state) {
            throw new Error('No pending auth state. Please try logging in again.');
        }

        const response = await axios.post(API_BASE + '/api/upstox/complete', { state }, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.data && response.data.success) {
            this.accessToken = response.data.accessToken;
            localStorage.setItem('upstox_access_token', response.data.accessToken);
            this._pendingState = null;
            return response.data;
        }

        throw new Error(response.data?.error || 'Login completion failed');
    },

    /**
     * Restore session from Clerk metadata (server-side).
     */
    async restoreSessionFromServer() {
        try {
            const token = await this._getClerkToken();
            if (!token) return false;

            const response = await axios.get(API_BASE + '/api/upstox/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                localStorage.setItem('upstox_access_token', response.data.accessToken);
                return true;
            }
            return false;
        } catch (error) {
            console.warn('[Upstox] Could not restore session:', error.message);
            return false;
        }
    },

    /**
     * Disconnect Upstox — clear local + Clerk metadata.
     */
    async disconnectFromServer() {
        try {
            const token = await this._getClerkToken();
            if (token) {
                await axios.post(API_BASE + '/api/upstox/disconnect', {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
        } catch (error) {
            console.warn('[Upstox] Could not clear session on server:', error.message);
        }
        this.clearSession();
    },

    /**
     * Get the Clerk JWT token for authenticated API calls.
     */
    async _getClerkToken() {
        try {
            if (window.__vue_clerk && window.__vue_clerk.getToken) {
                return await window.__vue_clerk.getToken();
            }
            if (window.Clerk && window.Clerk.session) {
                return await window.Clerk.session.getToken();
            }
            if (window.Clerk && window.Clerk.getToken) {
                return await window.Clerk.getToken();
            }
        } catch (e) {
            console.warn('[Upstox] Could not get Clerk token:', e.message);
        }
        return null;
    },

    /**
     * Check if user has a valid Upstox session.
     */
    isAuthenticated() {
        return !!(this.accessToken || localStorage.getItem('upstox_access_token'));
    },

    /**
     * Load saved session from localStorage.
     */
    loadSavedSession() {
        this.accessToken = localStorage.getItem('upstox_access_token');
        return this.accessToken;
    },

    /**
     * Clear session and remove stored credentials.
     */
    clearSession() {
        this.accessToken = null;
        this._pendingState = null;
        localStorage.removeItem('upstox_access_token');
        this.closeWebSocket();
    },

    /**
     * Fetch user profile to validate the token.
     */
    async getProfile() {
        try {
            const token = await this._getClerkToken();
            const response = await axios.get(API_BASE + '/api/upstox/profile', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            return response.data;
        } catch (error) {
            console.error('[Upstox] profile error:', error);
            throw error;
        }
    },

    async validateSession() {
        if (!this.isAuthenticated()) {
            return { valid: false, expired: false, neverConnected: true };
        }

        try {
            const token = await this._getClerkToken();
            if (!token) return { valid: false, expired: false };

            const response = await axios.get(API_BASE + '/api/upstox/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                localStorage.setItem('upstox_access_token', this.accessToken);
                return { valid: true, expired: false };
            }

            return { valid: false, expired: true };
        } catch (error) {
            const status = error.response && error.response.status;
            return { valid: false, expired: status === 401 || status === 403 };
        }
    },

    // ─── Symbol Search ───

    /**
     * Search symbols via Upstox Instrument Search API through backend proxy.
     */
    async searchSymbols(query, limit = 30) {
        if (!query || query.length < 1) return [];

        try {
            const token = await this._getClerkToken();
            if (!token) return [];

            const response = await axios.get(API_BASE + '/api/upstox/search', {
                params: {
                    query: query.toUpperCase(),
                    limit
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success) {
                this.symbols = response.data.symbols || [];
                return this.symbols;
            }
            return [];
        } catch (error) {
            console.error('[Upstox] search error:', error);
            return [];
        }
    },

    /**
     * Load symbol master — delegates to search for backward compatibility.
     */
    async loadSymbolMaster(exchange = 'NSE_EQ') {
        return this.searchSymbols('A', 100);
    },

    // ─── Historical Data ───

    /**
     * Load historical candle data through backend proxy.
     * Returns array of [timestamp, open, high, low, close, volume].
     */
    async loadHistoricalData(symbol, timeframe, _retryCount, endTime) {
        const retryCount = _retryCount || 0;

        if (!this.isAuthenticated()) {
            throw new Error('User not authenticated with Upstox');
        }

        if (!symbol || typeof symbol !== 'string' || symbol.trim() === '') {
            console.error('[Upstox] loadHistoricalData called with invalid symbol:', symbol);
            throw new Error('Symbol is required but not provided');
        }

        if (!timeframe || !TIMEFRAME_MAP[timeframe]) {
            console.error('[Upstox] Invalid timeframe:', timeframe);
            throw new Error(`Invalid timeframe: ${timeframe}. Supported: ${Object.keys(TIMEFRAME_MAP).join(', ')}`);
        }

        const interval = TIMEFRAME_MAP[timeframe];
        const instrumentKey = this._normalizeUpstoxSymbol(symbol.trim());
        const now = Number.isFinite(endTime) ? moment(endTime) : moment();
        const toDate = now.format('YYYY-MM-DD');
        const fromDate = this._calculateFromDate(timeframe, endTime);


        try {
            const token = await this._getClerkToken();
            if (!token) throw new Error('Not authenticated');

            const response = await axios.get(API_BASE + '/api/upstox/history', {
                params: {
                    instrument_key: instrumentKey,
                    interval,
                    from_date: fromDate,
                    to_date: toDate
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.success && response.data.data) {
                let candles = response.data.data.candles || [];

                // Convert Upstox format to app format: [timestamp, open, high, low, close, volume]
                candles = candles.map(c => {
                    const ts = moment(c[0]).valueOf();
                    return [ts, c[1], c[2], c[3], c[4], c[5]];
                });

                // Sort by timestamp ascending
                candles.sort((a, b) => a[0] - b[0]);

                // Aggregate if needed
                const aggFactor = AGGREGATION_MAP[timeframe];
                if (aggFactor && aggFactor > 1) {
                    candles = this._aggregateCandles(candles, aggFactor);
                }

                return candles;
            } else {
                const errMsg = response.data?.error || 'Failed to load historical data';
                throw new Error(errMsg);
            }
        } catch (error) {
            const status = error.response && error.response.status;
            const authExpired = error.response?.data?.authExpired;

            if (status === 401 && authExpired) {
                console.error('[Upstox] Access token expired, clearing session');
                this.clearSession();
                throw new Error('Upstox session expired. Please reconnect your Upstox account.');
            }

            // Retry on rate-limit (429) or server errors (5xx)
            if (retryCount < 2 && (status === 429 || (status >= 500 && status < 600))) {
                const backoff = (retryCount + 1) * 2000;
                console.warn(`[Upstox] Rate limited or server error (${status}), retrying in ${backoff}ms...`);
                await new Promise(r => setTimeout(r, backoff));
                return this.loadHistoricalData(symbol, timeframe, retryCount + 1, endTime);
            }

            console.error('[Upstox] loadHistoricalData error:', error.message);
            throw error;
        }
    },

    /**
     * Calculate the from date based on timeframe.
     */
    _calculateFromDate(timeframe, endTime) {
        const now = Number.isFinite(endTime) ? moment(endTime) : moment();

        // Upstox API date range limits:
        // 1minute: last 1 month (30 days)
        // 30minute: last 1 year (365 days)
        // day: last 1 year (365 days)
        // week: last 10 years
        // month: last 10 years

        // Optimal days to fetch per timeframe (balances data vs performance)
        // Similar to Fyers approach
        const interval = TIMEFRAME_MAP[timeframe] || '1minute';

        const OPTIMAL_DAYS = {
            '1m': 5,
            '3m': 10,
            '5m': 10,
            '10m': 15,
            '15m': 25,
            '20m': 45,
            '30m': 45,
            '1H': 60,
            '2H': 75,
            '4H': 90,
            '1D': 365,
            '1W': 365 * 3,
            '1M': 365 * 10
        };

        // Max days allowed by Upstox API per interval
        const MAX_DAYS = {
            '1minute': 30,
            '30minute': 365,
            'day': 365,
            'week': 365 * 10,
            'month': 365 * 10
        };

        const optimalDays = OPTIMAL_DAYS[timeframe] || 30;
        const maxDays = MAX_DAYS[interval] || 365;
        const safeDays = Math.min(optimalDays, maxDays);

        const fromDate = now.clone().subtract(safeDays, 'days');


        return fromDate.format('YYYY-MM-DD');
    },

    /**
     * Aggregate candles by time boundaries.
     * Groups candles into intervals based on the target timeframe duration,
     * respecting market session boundaries (doesn't combine across gaps).
     */
    _aggregateCandles(candles, factor) {
        if (!candles || candles.length === 0 || factor <= 1) return candles;

        // Calculate the target interval in milliseconds
        // factor represents how many source candles make one target candle
        // Source is always 1-minute (60000ms) for minute-based, or 30-minute for hour-based
        const sourceInterval = candles.length > 1 ? (candles[1][0] - candles[0][0]) : 60000;
        const targetInterval = sourceInterval * factor;

        const aggregated = [];
        let currentGroup = [];
        let currentBoundary = 0;

        for (let i = 0; i < candles.length; i++) {
            const candle = candles[i];
            const ts = candle[0];

            // Calculate which time boundary this candle belongs to
            const boundary = ts - (ts % targetInterval);

            if (boundary !== currentBoundary && currentGroup.length > 0) {
                // Flush the current group
                aggregated.push(this._combineGroup(currentGroup));
                currentGroup = [];
            }

            currentBoundary = boundary;
            currentGroup.push(candle);
        }

        // Don't forget the last group
        if (currentGroup.length > 0) {
            aggregated.push(this._combineGroup(currentGroup));
        }

        return aggregated;
    },

    /**
     * Combine a group of candles into a single OHLCV candle.
     */
    _combineGroup(group) {
        if (group.length === 0) return null;
        if (group.length === 1) return group[0];

        const first = group[0];
        const last = group[group.length - 1];
        let high = first[2];
        let low = first[3];
        let volume = 0;

        for (const c of group) {
            high = Math.max(high, c[2]);
            low = Math.min(low, c[3]);
            volume += c[5] || 0;
        }

        return [
            first[0],   // timestamp of first candle (period start)
            first[1],   // open of first
            high,        // highest high
            low,         // lowest low
            last[4],     // close of last
            volume       // sum of volumes
        ];
    },

    // ─── WebSocket (Real-time Data) ───

    /**
     * Connect to Upstox Market Data WebSocket.
     * Upstox requires getting a WebSocket URL via their authorize endpoint first.
     */
    async connectWebSocket(symbols, onMessage) {
        if (!this.isAuthenticated()) {
            console.error('[Upstox] Cannot connect WebSocket: not authenticated');
            return null;
        }

        this.closeWebSocket();

        try {
            const token = await this._getClerkToken();
            if (!token) {
                console.error('[Upstox] Missing Clerk token for WebSocket');
                return null;
            }

            // Get WebSocket authorized URL from server
            const response = await axios.get(API_BASE + '/api/upstox/ws-url', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.data || !response.data.success || !response.data.wsUrl) {
                console.error('[Upstox] Failed to get WebSocket URL');
                return null;
            }

            const wsUrl = response.data.wsUrl;

            this._wsSymbols = symbols;
            this._wsOnMessage = onMessage;

            this.ws = new WebSocket(wsUrl);
            this.ws.binaryType = 'arraybuffer';

            this.ws.onopen = () => {
                this.reconnectAttempts = 0;

                // Subscribe to symbols
                if (symbols && Array.isArray(symbols)) {
                    this._subscribeSymbols(symbols);
                }
            };

            this.ws.onmessage = (event) => {
                try {
                    let data;
                    if (typeof event.data === 'string') {
                        // Text message — try JSON parse
                        try {
                            data = JSON.parse(event.data);
                        } catch (e) {
                            console.warn('[Upstox] Non-JSON text message:', event.data.substring(0, 100));
                            return;
                        }
                    } else if (event.data instanceof ArrayBuffer) {
                        // V3 sends protobuf binary — try to decode as JSON first
                        // (some messages like subscription confirmations may be JSON)
                        const bytes = new Uint8Array(event.data);
                        try {
                            const text = new TextDecoder().decode(bytes);
                            data = JSON.parse(text);
                        } catch (e) {
                            // Protobuf binary — attempt lightweight decode
                            data = this._parseProtobufMessage(bytes);
                        }
                    } else if (event.data instanceof Blob) {
                        // Handle Blob by converting to ArrayBuffer
                        event.data.arrayBuffer().then(buffer => {
                            const bytes = new Uint8Array(buffer);
                            try {
                                const text = new TextDecoder().decode(bytes);
                                const parsed = JSON.parse(text);
                                const normalized = this._normalizeWsMessage(parsed);
                                if (normalized && onMessage) onMessage(normalized);
                            } catch (e) {
                                const parsed = this._parseProtobufMessage(bytes);
                                if (parsed) {
                                    const normalized = this._normalizeWsMessage(parsed);
                                    if (normalized && onMessage) onMessage(normalized);
                                }
                            }
                        });
                        return;
                    }

                    if (data && onMessage) {
                        const normalized = this._normalizeWsMessage(data);
                        if (normalized) onMessage(normalized);
                    }
                } catch (error) {
                    console.error('[Upstox] Error processing WS message:', error);
                }
            };

            this.ws.onerror = (error) => {
                console.error('[Upstox] WebSocket error:', error);
                if (this._wsOnError) this._wsOnError(error);
            };

            this.ws.onclose = (event) => {

                if (event.code === 1000 || !this.isAuthenticated()) {
                    return;
                }

                // Reconnect with exponential backoff
                if (this.reconnectAttempts < 5) {
                    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
                    this.reconnectAttempts++;
                    setTimeout(() => {
                        this.connectWebSocket(this._wsSymbols, this._wsOnMessage);
                    }, delay);
                }
            };

            return this.ws;
        } catch (error) {
            console.error('[Upstox] WebSocket connection error:', error);
            return null;
        }
    },

    /**
     * Subscribe to symbols on the WebSocket.
     * Upstox subscription format uses instrument keys.
     */
    _subscribeSymbols(symbols) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[Upstox] WebSocket not connected, cannot subscribe');
            return;
        }

        const instrumentKeys = symbols.map(s => this._normalizeUpstoxSymbol(s)).filter(Boolean);
        if (instrumentKeys.length === 0) return;

        // Upstox WebSocket subscription message
        const subscribeMsg = {
            guid: 'tradingcandle_' + Date.now(),
            method: 'sub',
            data: {
                mode: 'full',  // Full market data with OHLC
                instrumentKeys: instrumentKeys
            }
        };

        try {
            // V3 requires binary format (UTF-8 encoded JSON)
            const msgStr = JSON.stringify(subscribeMsg);
            const encoder = new TextEncoder();
            this.ws.send(encoder.encode(msgStr));
        } catch (error) {
            console.error('[Upstox] Failed to send subscription:', error);
        }
    },

    /**
     * Subscribe to a single symbol.
     */
    subscribeToSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[Upstox] WebSocket not connected, cannot subscribe to:', symbol);
            return;
        }

        const instrumentKey = this._normalizeUpstoxSymbol(symbol);
        if (!instrumentKey) return;

        const subscribeMsg = {
            guid: 'tradingcandle_' + Date.now(),
            method: 'sub',
            data: {
                mode: 'full',
                instrumentKeys: [instrumentKey]
            }
        };

        try {
            // V3 requires binary format (UTF-8 encoded JSON)
            const msgStr = JSON.stringify(subscribeMsg);
            const encoder = new TextEncoder();
            this.ws.send(encoder.encode(msgStr));
        } catch (error) {
            console.error('[Upstox] Failed to subscribe:', error);
        }
    },

    /**
     * Unsubscribe from a symbol.
     */
    unsubscribeFromSymbol(symbol) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[Upstox] WebSocket not connected, cannot unsubscribe from:', symbol);
            return;
        }

        const instrumentKey = this._normalizeUpstoxSymbol(symbol);
        if (!instrumentKey) return;

        const unsubscribeMsg = {
            guid: 'tradingcandle_' + Date.now(),
            method: 'unsub',
            data: {
                mode: 'full',
                instrumentKeys: [instrumentKey]
            }
        };

        try {
            // V3 requires binary format (UTF-8 encoded JSON)
            const msgStr = JSON.stringify(unsubscribeMsg);
            const encoder = new TextEncoder();
            this.ws.send(encoder.encode(msgStr));
        } catch (error) {
            console.error('[Upstox] Failed to unsubscribe:', error);
        }
    },

    /**
     * Close WebSocket connection.
     */
    closeWebSocket() {
        if (this.ws) {
            this.ws.onclose = null;
            this.ws.close(1000, 'Manual close');
            this.ws = null;
        }
        this.reconnectAttempts = 0;
        this._wsSymbols = null;
    },

    destroyWs() {
        this.closeWebSocket();
    },

    /**
     * Alias for connectWebSocket for backward compatibility.
     */
    createWebSocket(onMessage, onError, symbols) {
        this._wsOnError = onError;
        return this.connectWebSocket(symbols || null, onMessage);
    },

    // ─── Utility ───

    /**
     * Normalize a symbol to Upstox instrument_key format.
     * Upstox uses: NSE_EQ|INE002A01018 or NSE_EQ|RELIANCE
     * For simplicity, we accept formats like:
     *   - NSE_EQ|INE002A01018 (already normalized)
     *   - NSE:RELIANCE (convert to NSE_EQ|RELIANCE)
     *   - RELIANCE (assume NSE_EQ|RELIANCE)
     */
    _normalizeUpstoxSymbol(symbol) {
        if (!symbol) return '';

        // Already in Upstox format with pipe separator
        if (symbol.includes('|')) {
            return symbol;
        }

        // Has exchange prefix with colon (e.g., NSE:RELIANCE)
        if (symbol.includes(':')) {
            const parts = symbol.split(':');
            const exch = parts[0].toUpperCase();
            const sym = parts[1];

            // Map exchange names to Upstox segment format
            const exchMap = {
                'NSE': 'NSE_EQ',
                'BSE': 'BSE_EQ',
                'NFO': 'NSE_FO',
                'BFO': 'BSE_FO',
                'MCX': 'MCX_FO',
                'NSE_EQ': 'NSE_EQ',
                'BSE_EQ': 'BSE_EQ',
                'NSE_FO': 'NSE_FO',
                'NSE_INDEX': 'NSE_INDEX'
            };

            const segment = exchMap[exch] || 'NSE_EQ';

            // Remove suffixes like -EQ, -INDEX from Fyers format
            const cleanSym = sym.replace(/-EQ$/, '').replace(/-INDEX$/, '').replace(/-FUT$/, '');
            return segment + '|' + cleanSym;
        }

        // Plain symbol — assume NSE equity
        return 'NSE_EQ|' + symbol.toUpperCase();
    },

    /**
     * Convert Upstox instrument_key to display format.
     */
    getDisplaySymbol(instrumentKey) {
        if (!instrumentKey) return '';
        const parts = instrumentKey.split('|');
        return parts.length > 1 ? parts[1] : parts[0];
    },

    /**
     * Parse protobuf binary message from Upstox V3 WebSocket.
     * Uses protobufjs to decode the FeedResponse message.
     */
    _parseProtobufMessage(bytes) {
        if (!bytes || bytes.length < 4) return null;

        const decoded = decodeUpstoxFeed(bytes);
        if (!decoded) {
            return null;
        }

        // Skip market_info messages (connection status)
        if (decoded.type === 'market_info' || decoded.type === 1) {
            return null;
        }

        // Skip initial_feed / empty feeds
        if (!decoded.feeds || Object.keys(decoded.feeds).length === 0) {
            return null;
        }

        // Return the decoded feed data (type: live_feed)
        return decoded;
    },

    /**
     * Normalize WebSocket message to common format used by the chart.
     * Handles both V2 JSON and V3 protobuf-decoded structures.
     */
    _normalizeWsMessage(data) {
        if (!data) return null;

        // V3 protobuf decoded: { type: "live_feed", feeds: {...}, currentTs: "..." }
        // V2 JSON: { feeds: { "NSE_EQ|INE002A01018": { ltpc: {...} } } }
        if (data.feeds) {
            const results = [];
            for (const [key, feed] of Object.entries(data.feeds)) {
                let ltp = 0;
                let ltt = Date.now();
                let ltq = 0;
                let cp = 0;
                let volume = 0;

                // Prefer full feed (ff.marketFF) if available — has more data
                if (feed.ff && feed.ff.marketFF && feed.ff.marketFF.ltpc) {
                    const mfLtpc = feed.ff.marketFF.ltpc;
                    ltp = Number(mfLtpc.ltp) || 0;
                    ltt = Number(mfLtpc.ltt) || Date.now();
                    ltq = Number(mfLtpc.ltq) || 0;
                    cp = Number(mfLtpc.cp) || 0;
                    // Get volume from extended feed details
                    if (feed.ff.marketFF.eFeedDetails) {
                        volume = Number(feed.ff.marketFF.eFeedDetails.vtt) || 0;
                    }
                } else if (feed.ltpc) {
                    // Fallback to ltpc-only feed
                    ltp = Number(feed.ltpc.ltp) || 0;
                    ltt = Number(feed.ltpc.ltt) || Date.now();
                    ltq = Number(feed.ltpc.ltq) || 0;
                    cp = Number(feed.ltpc.cp) || 0;
                }

                if (ltp > 0) {
                    results.push({
                        symbol: key,
                        ltp: ltp,
                        timestamp: ltt,
                        volume: volume || ltq,
                        close_price: cp,
                        type: 'upstox_tick'
                    });
                }
            }
            return results.length === 1 ? results[0] : results.length > 0 ? results : null;
        }

        return data;
    },

    /**
     * Check if Upstox authentication is completed (for Tauri polling)
     * Returns authentication status from server
     */
    async checkAuthStatus() {
        try {
            const token = await this._getClerkToken();
            if (!token) return { success: false };

            const response = await axios.get(API_BASE + '/api/upstox/session', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data && response.data.connected && response.data.accessToken) {
                this.accessToken = response.data.accessToken;
                this.appId = response.data.appId;

                localStorage.setItem('upstox_access_token', this.accessToken);
                localStorage.setItem('upstox_app_id', this.appId);

                return { success: true, accessToken: this.accessToken };
            }

            return { success: false };
        } catch (error) {
            return { success: false };
        }
    }
};
